import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, Button, Alert, CircularProgress } from '@mui/material';
import { solidBtn, ghostBtn, field } from '../uiKit';
import { localToday } from './ExpenseReminders';

const blank = () => ({ type: 'Prepaid', employeeCode: '', shoot: '', date: localToday(), amount: '', notes: '' });

/**
 * "Add Payment" for the shoot ledger. A payment belongs to an employee (Prepaid advance or Postpaid
 * settlement) and adds to their running total; it is not tied to a particular expense.
 * `employees` is [{ code, name }]; `onSave` returns a promise (rejects with the server's message).
 */
export default function PaymentDialog({ open, onClose, onSave, employees = [], loadShoots, employeeCode = '' }) {
    const [pay, setPay] = useState(blank);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [shoots, setShoots] = useState([]);
    const [loadingShoots, setLoadingShoots] = useState(false);

    useEffect(() => { if (open) { setPay({ ...blank(), employeeCode }); setErr(''); } }, [open, employeeCode]);

    // Postpaid settles a shoot already expensed against this employee, so the choices are that employee's own.
    useEffect(() => {
        if (!open || pay.type !== 'Postpaid' || !pay.employeeCode || !loadShoots) { setShoots([]); return undefined; }
        let live = true;
        setLoadingShoots(true);
        loadShoots(pay.employeeCode)
            .then((list) => { if (live) setShoots(list); })
            .catch(() => { if (live) setShoots([]); })
            .finally(() => { if (live) setLoadingShoots(false); });
        return () => { live = false; };
    }, [open, pay.type, pay.employeeCode, loadShoots]);

    const set = (k) => (e) => setPay((p) => ({ ...p, [k]: e.target.value }));
    const valid = pay.employeeCode && pay.shoot.trim() && Number(pay.amount) > 0 && pay.date;

    const save = async () => {
        setSaving(true);
        setErr('');
        try {
            await onSave({ ...pay, amount: Number(pay.amount) });
            onClose();
        } catch (e) {
            setErr(e.message || 'Could not save the payment.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>Add payment</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <Stack spacing={2}>
                    <TextField select label="Type" size="small" fullWidth value={pay.type} onChange={(e) => setPay((p) => ({ ...p, type: e.target.value, shoot: '' }))} sx={field}>
                        <MenuItem value="Prepaid">Prepaid (advance)</MenuItem>
                        <MenuItem value="Postpaid">Postpaid</MenuItem>
                    </TextField>
                    <TextField select required label="Employee" size="small" fullWidth value={pay.employeeCode} onChange={(e) => setPay((p) => ({ ...p, employeeCode: e.target.value, shoot: p.type === 'Postpaid' ? '' : p.shoot }))} sx={field}
                        helperText={employees.length ? undefined : 'No active employees found.'}>
                        {employees.map((o) => <MenuItem key={o.code} value={o.code} sx={{ fontSize: 13 }}>{o.name} ({o.code})</MenuItem>)}
                    </TextField>
                    {pay.type === 'Prepaid' ? (
                        <TextField required label="Shoot name" size="small" fullWidth value={pay.shoot} onChange={set('shoot')} sx={field}
                            helperText="Type the shoot this advance is for." />
                    ) : (
                        <TextField select required label="Shoot" size="small" fullWidth value={pay.shoot} onChange={set('shoot')} sx={field}
                            disabled={!pay.employeeCode || loadingShoots}
                            helperText={!pay.employeeCode ? 'Pick an employee first.' : loadingShoots ? 'Loading…' : shoots.length ? undefined : 'No shoot expenses logged for this employee yet.'}>
                            {shoots.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                        </TextField>
                    )}
                    <TextField label="Amount (₹)" type="number" size="small" fullWidth value={pay.amount} onChange={set('amount')} sx={field} slotProps={{ htmlInput: { min: 0 } }} />
                    <TextField label="Date" type="date" size="small" fullWidth value={pay.date} onChange={set('date')} sx={field}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { onClick: (e) => { try { e.currentTarget.showPicker(); } catch { /* needs a user gesture; focus is enough */ } }, style: { cursor: 'pointer' } } }} />
                    <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={pay.notes} onChange={set('notes')} sx={field} />
                    {err && <Alert severity="error">{err}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                <Button onClick={save} disabled={!valid || saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
